import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { Camera, Plus, X } from 'lucide-react';
import { resolveCaptureFrames, type CaptureFrameImage } from '@/lib/capture-frames-source';

/**
 * The photographs on a delivered capture, as the frame strip of the result
 * screen (wireframe R-A, plan/smart-capture/wireframes/mobile-fit.html).
 *
 * The frames render from the device where the queue record still holds the
 * bytes, and from `GET /capture/{id}/assets` otherwise
 * (src/lib/capture-frames-source.ts), so the operator confirming a value
 * can see the item it was read off even with no signal.
 *
 * Each frame is a 64px thumbnail in one row, with an Add tile while the
 * capture holds fewer than three. A thumbnail opens the frame full screen
 * inside the app rather than in a new tab: this client installs to the home
 * screen, so a new tab throws the operator out of the app, and a device
 * frame is an object URL this component revokes when the screen closes.
 * Retake sits on the full-screen view, where the operator can see which
 * frame they are replacing.
 *
 * Required fields that came back empty are named beside the strip
 * (INTERFACES.md section 5), with an Add frame action that opens the same
 * picker as the Add tile, because a missing model number is most often a
 * rating plate nobody photographed.
 *
 * A read URL expires. A frame that fails to load asks for fresh URLs once
 * rather than leaving a broken image on a confirmation screen. Every frame
 * is signed in the same request, so they expire together.
 */

interface CaptureFramesProps {
  captureId: string;
  /** From the capture record, in capture order. */
  assetKeys: string[];
  /** A replacement photograph for the frame at `index`. Omit to hide Retake. */
  onRetake?: (index: number, file: File) => void;
  /** A new photograph appended to the capture. Omit to hide Add. */
  onAddFrame?: (file: File) => void;
  /** Frames the capture may hold, `MAX_ASSETS_PER_CAPTURE` on the API. */
  maxFrames?: number;
  /** Disables Retake and Add while a change is being sent. */
  busy?: boolean;
  /**
   * Skip the device copy and read the frames from the server. Set once a
   * frame has been retaken: the queue record still holds the old bytes
   * under the same frame count, and would show the photograph that was
   * replaced.
   */
  preferServer?: boolean;
  /** Labels of the required fields that came back empty. */
  missing?: string[];
}

export function CaptureFrames({
  captureId,
  assetKeys,
  onRetake,
  onAddFrame,
  maxFrames = 3,
  busy = false,
  preferServer = false,
  missing = [],
}: CaptureFramesProps) {
  // The record is re-read every 2.5s while extraction runs, so the array
  // arrives with a new identity each poll while naming the same frames.
  // Joining it gives the effect a dependency that changes when the frames
  // do, rather than one that changes on the clock.
  const keyList = assetKeys.join('\n');
  const [frames, setFrames] = useState<CaptureFrameImage[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [refreshes, setRefreshes] = useState(0);
  const [zoomed, setZoomed] = useState<number | null>(null);
  const pickerRef = useRef<HTMLInputElement | null>(null);
  const pickTargetRef = useRef<{ action: 'retake'; index: number } | { action: 'add' } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const made: string[] = [];
    setStatus('loading');

    resolveCaptureFrames(captureId, keyList ? keyList.split('\n') : [], {
      createObjectUrl: (blob) => {
        const url = URL.createObjectURL(blob);
        made.push(url);
        return url;
      },
      ...(preferServer ? { findQueued: () => Promise.resolve(undefined) } : {}),
    })
      .then((resolved) => {
        if (cancelled) return;
        setFrames(resolved);
        setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) setStatus('error');
      });

    return () => {
      cancelled = true;
      for (const url of made) URL.revokeObjectURL(url);
    };
  }, [captureId, keyList, refreshes, preferServer]);

  // One refresh, whichever frame reports the failure first.
  const refreshOnce = useCallback(
    (frame: CaptureFrameImage) => {
      if (frame.source === 'server' && refreshes === 0) setRefreshes(1);
    },
    [refreshes],
  );

  const close = useCallback(() => setZoomed(null), []);
  useEffect(() => {
    if (zoomed === null) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomed, close]);

  function pick(target: { action: 'retake'; index: number } | { action: 'add' }) {
    pickTargetRef.current = target;
    pickerRef.current?.click();
  }

  function picked(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    const target = pickTargetRef.current;
    // Cleared so choosing the same photograph twice still fires a change.
    event.target.value = '';
    pickTargetRef.current = null;
    if (!file || !target) return;
    if (target.action === 'retake') {
      setZoomed(null);
      onRetake?.(target.index, file);
    } else onAddFrame?.(file);
  }

  const canAdd = Boolean(onAddFrame) && assetKeys.length < maxFrames;
  const current = zoomed === null ? undefined : frames[zoomed];
  const frameName = (index: number) => (frames.length > 1 ? `Frame ${index + 1}` : 'Frame');

  return (
    <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
      {/* One hidden picker serves every Retake and Add. `capture` asks a
          phone for the rear camera; a desktop browser ignores it and opens
          a file chooser. */}
      {(onRetake || onAddFrame) && (
        <input
          ref={pickerRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          tabIndex={-1}
          aria-hidden="true"
          onChange={picked}
        />
      )}

      <ul aria-label="Frames" className="flex flex-none gap-2">
        {assetKeys.map((assetKey, index) => {
          const frame = frames[index];
          return (
            <li key={assetKey}>
              {frame ? (
                <button
                  type="button"
                  aria-label={`Open ${frameName(index).toLowerCase()}`}
                  onClick={() => setZoomed(index)}
                  className="block size-16 overflow-hidden rounded-control border border-border-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                  <img src={frame.url} alt="" className="size-full object-cover" onError={() => refreshOnce(frame)} />
                </button>
              ) : (
                <span
                  className="flex size-16 items-end justify-center rounded-control border border-border-strong pb-1 text-[10px] text-muted"
                  aria-label={status === 'error' ? 'Frame unavailable' : 'Loading frame'}
                >
                  {status === 'error' ? 'Unavailable' : frameName(index)}
                </span>
              )}
            </li>
          );
        })}
        {canAdd && (
          <li>
            <button
              type="button"
              onClick={() => pick({ action: 'add' })}
              disabled={busy}
              aria-label="Add a frame"
              className="flex size-16 flex-col items-center justify-center gap-0.5 rounded-control border border-dashed border-border-strong text-[10px] text-muted hover:text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
            >
              <Plus className="size-5" aria-hidden="true" />
              Add
            </button>
          </li>
        )}
      </ul>

      {missing.length > 0 && (
        <div className="flex min-w-[8.5rem] flex-1 flex-col items-start gap-1" role="status" aria-label="Missing fields">
          <ul className="flex flex-col text-xs text-warning">
            {missing.map((label) => (
              <li key={label}>{label} missing</li>
            ))}
          </ul>
          {canAdd && (
            <button
              type="button"
              onClick={() => pick({ action: 'add' })}
              disabled={busy}
              className="flex min-h-11 items-center gap-2 rounded-control border border-border-strong px-3 text-sm text-surface-foreground hover:border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
            >
              <Camera className="size-4" aria-hidden="true" />
              Add frame
            </button>
          )}
        </div>
      )}

      {current && zoomed !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={frameName(zoomed)}
          className="fixed inset-0 z-50 flex flex-col bg-surface"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          onClick={close}
        >
          <div className="flex flex-none items-center justify-between gap-2 p-2">
            <p className="pl-2 text-sm text-muted">
              {frames.length > 1 ? `Frame ${zoomed + 1} of ${frames.length}` : 'Frame'}
            </p>
            <button
              type="button"
              onClick={close}
              aria-label="Close the photograph"
              className="flex size-11 items-center justify-center rounded-control text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
              <X className="size-5" aria-hidden="true" />
            </button>
          </div>
          <img
            src={current.url}
            alt={frameName(zoomed)}
            className="min-h-0 flex-1 object-contain"
            onError={() => refreshOnce(current)}
          />
          {onRetake && (
            <div className="flex flex-none justify-center p-3">
              <button
                type="button"
                disabled={busy}
                onClick={(event) => {
                  event.stopPropagation();
                  pick({ action: 'retake', index: zoomed });
                }}
                className="flex min-h-12 items-center justify-center gap-2 rounded-control border border-border-strong px-6 text-sm text-surface-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-50"
              >
                <Camera className="size-4" aria-hidden="true" />
                Retake
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
